import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OrderSummary = ({ 
  subtotal, 
  shipping, 
  tax, 
  discount = 0, 
  total, 
  onApplyPromoCode, 
  onProceedToCheckout,
  isCheckoutLoading = false 
}) => {
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const handleApplyPromoCode = () => {
    if (!promoCode?.trim()) { setPromoError('Please enter a promo code'); return; }
    setPromoError('Promo codes are not available yet — check back soon.');
    setPromoSuccess('');
  };

  const handleProceedToCheckout = () => {
    if (onProceedToCheckout) {
      onProceedToCheckout();
    }
  };

  const freeShippingThreshold = 50;
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - subtotal);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1 sticky top-24">
      <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
        Order Summary
      </h2>
      {/* Free Shipping Progress */}
      {remainingForFreeShipping > 0 && (
        <div className="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Truck" size={16} className="text-accent" />
            <span className="text-sm font-medium text-accent-foreground">
              Free Shipping Available
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            Add ${remainingForFreeShipping?.toFixed(2)} more to qualify for free shipping
          </p>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-accent h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, (subtotal / freeShippingThreshold) * 100)}%` }}
            />
          </div>
        </div>
      )}
      {/* Promo Code Section */}
      <div className="mb-6">
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e?.target?.value?.toUpperCase())}
              error={promoError}
              disabled={isApplyingPromo}
            />
          </div>
          <Button
            variant="outline"
            onClick={handleApplyPromoCode}
            loading={isApplyingPromo}
            disabled={!promoCode?.trim()}
          >
            Apply
          </Button>
        </div>
        {promoSuccess && (
          <div className="mt-2 p-2 bg-success/10 border border-success/20 rounded-md">
            <p className="text-sm text-success">{promoSuccess}</p>
          </div>
        )}
      </div>
      {/* Price Breakdown */}
      <div className="space-y-3 mb-6">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-foreground font-medium">${subtotal?.toFixed(2)}</span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Shipping</span>
          <span className="text-foreground font-medium">
            {shipping === 0 ? (
              <span className="text-success">Free</span>
            ) : (
              `$${shipping?.toFixed(2)}`
            )}
          </span>
        </div>
        
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Tax</span>
          <span className="text-foreground font-medium">${tax?.toFixed(2)}</span>
        </div>
        
        {discount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Discount</span>
            <span className="text-success font-medium">-${discount?.toFixed(2)}</span>
          </div>
        )}
        
        <div className="border-t border-border pt-3">
          <div className="flex justify-between">
            <span className="text-lg font-semibold text-foreground">Total</span>
            <span className="text-lg font-semibold text-foreground">${total?.toFixed(2)}</span>
          </div>
        </div>
      </div>
      {/* Estimated Delivery */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Calendar" size={16} className="text-primary" />
          <span className="text-sm font-medium text-foreground">Estimated Delivery</span>
        </div>
        <p className="text-sm text-muted-foreground">
          {shipping === 0 ? 'Nov 22-24, 2024' : 'Nov 25-27, 2024'}
        </p>
      </div>
      {/* Security Badges */}
      <div className="mb-6">
        <div className="flex items-center justify-center gap-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-1">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">SSL Secure</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="CreditCard" size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">Safe Payment</span>
          </div>
        </div>
      </div>
      {/* Checkout Button */}
      <Button
        fullWidth
        size="lg"
        onClick={handleProceedToCheckout}
        loading={isCheckoutLoading}
        iconName="Lock"
        iconPosition="left"
        className="mb-4"
      >
        Secure Checkout
      </Button>
      {/* Continue Shopping */}
      <Button
        variant="outline"
        fullWidth
        onClick={() => window.location.href = '/product-catalog'}
      >
        Continue Shopping
      </Button>
    </div>
  );
};

export default OrderSummary;